<template>
    <div
        class="l-ss-c-image-search-container"
        :class="{ 'l-ss-c-image-search-container-mobile': $screen.isMobile }">
        <input
            ref="image-picker"
            accept="image/*,application/*"
            style="display: none;"
            type="file"
            @change="onFileChange">
        <div class="l-ss-c-image-search-row">
            <div class="l-ss-c-image-search-column">
                <div
                    ref="drop-zone"
                    :class="{
                        'l-ss-c-image-search-drop-zone-active': dropZoneActive,
                        'l-ss-c-image-search-drop-zone-pre-active': isDragStart }"
                    class="l-ss-c-image-search-drop-zone">
                    <div class="l-ss-c-image-search-drop-zone-header">
                        <i v-html="galleryIcon"></i>
                        <p
                            class="l-ss-c-text-caption l-ss-c-image-search-drop-zone-text-caption">
                            {{
                                !$screen.isMobile ? $t('imageSearch.dropTitleDesktop') : $t('imageSearch.dropTitleMobile')
                            }}
                        </p>
                    </div>
                    <div class="l-ss-c-image-search-drop-zone-actions">
                        <button
                            class="l-ss-c-button l-ss-c-button-large"
                            :disabled="loadByUrlStatus.loading"
                            @click="onOpenFilePicker($event)">
                            {{ $t('imageSearch.selectFileButton') }}
                        </button>
                        <button
                            v-if="!$screen.isMobile"
                            class="l-ss-c-button l-ss-c-button-outline l-ss-c-button-large"
                            :disabled="loadByUrlStatus.loading"
                            @click="insertImageFromClipboard">
                            {{ $t('imageSearch.pasteFromClipboard') }}
                        </button>
                    </div>
                </div>
                <div
                    class="l-ss-c-image-search-error"
                    v-if="error">
                    {{ error }}
                </div>
            </div>

            <div class="l-ss-c-image-search-spacer">
                <i></i>
                <span>{{ $t('or') }}</span>
                <i></i>
            </div>

            <div class="l-ss-c-image-search-column">
                <div class="l-ss-c-image-search-link-zone">
                    <app-input
                        class="l-ss-c-image-search-link-input"
                        :placeholder="$t('imageSearch.pasteLink')"
                        show-search-icon
                        type="text"
                        v-model="imageLink"
                        :loading="loadByUrlStatus.loading"
                        :error="loadByUrlStatus.error"
                        @input.native="onLinkChange"/>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { IMAGE_PROXY_URL } from '../../config';
    import debounce from '../helpers/debounce';
    import AppInput from '@/core/components/common/app-input.vue';

    import getDataFromClipboard from '@/core/helpers/get-data-from-clipboard';

    import galleryIcon from '@/core/assets/images/icons/gallery.svg';

    const HANDLE_LINK_CHANGE_DELAY = 300;

    const AVAILABLE_IMAGE_FORMATS = ['png', 'gif', 'webp', 'jpeg', 'jpg'].map((format) => `image/${format}`);

    export default {
        components: {
            AppInput,
        },
        data() {
            return {
                loadByUrlStatus: {
                    loading: false,
                    error: null,
                },
                error: '',
                linkTimer: null,
                imageLink: '',
                dropZone: null,
                dropZoneActive: false,
                isDragStart: false,
            };
        },
        computed: {
            galleryIcon() {
                return galleryIcon;
            },
        },
        mounted() {
            this.addDragListeners();
        },
        beforeDestroy() {
            window.removeEventListener('dragenter', this.highlightDropZone);
            window.removeEventListener('dragleave', this.backlightDropZone);
            window.removeEventListener('mouseout', this.backlightDropZone);
        },
        methods: {
            addDragListeners() {
                const dropContainer = this.$refs['drop-zone'];

                window.addEventListener('dragenter', this.highlightDropZone);
                window.addEventListener('dragleave', this.backlightDropZone);
                window.addEventListener('mouseout', this.backlightDropZone);

                dropContainer.ondragover = dropContainer.ondragenter = (event) => {
                    event.preventDefault();
                    this.isDragStart = true;
                    this.dropZoneActive = true;
                };

                dropContainer.ondragleave = (event) => {
                    event.preventDefault();
                    this.dropZoneActive = false;
                };

                dropContainer.ondrop = (event) => {
                    event.preventDefault();
                    this.imageSearchButtonSelectFileClick(event);

                    this.clearLoadImageByUrlError();

                    this.dropZoneActive = false;

                    if (event.dataTransfer.files.length === 0) {
                        this.error = this.$t('imageSearch.errors.noImage');
                        return;
                    }

                    const image = event.dataTransfer.files[0];

                    if (!this.processImageType(image)) {
                        return;
                    }

                    this.readAsBase64(image)
                        .then(base64 => {
                            this.handleSearch(base64);
                        })
                        .catch(error => {
                            this.error = String(error.message || error.statusText || error);
                        });
                };
            },
            highlightDropZone() {
                this.isDragStart = true;
            },
            backlightDropZone() {
                this.isDragStart = false;
            },
            readAsBase64(file) {
                return new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onloadend = function () {
                        resolve(reader.result);
                    };
                    reader.onerror = function (error) {
                        reject(error);
                    };
                    reader.readAsDataURL(file);
                });
            },
            onOpenFilePicker(event) {
                this.clearLoadImageByUrlError();

                this.$refs['image-picker'].click();
                this.imageSearchButtonSelectFileClick(event);
            },
            imageSearchButtonSelectFileClick(event) {
                this.$emit('image-search-button-select-file-click', {
                    type: 'imageSearchButtonSelectFileClick',
                    event,
                });
            },
            processImageType(image) {
                if (!AVAILABLE_IMAGE_FORMATS.includes(image.type)) {
                    this.error = this.$t('imageSearch.errors.invalidImageFormat');
                    return false;
                }

                return true;
            },
            clearLoadImageByUrlError() {
                this.imageLink = '';
                this.loadByUrlStatus = {
                    loading: false,
                    error: null,
                };
            },
            async insertImageFromClipboard() {
                try {
                    this.clearLoadImageByUrlError();

                    const imageBlob = await getDataFromClipboard(AVAILABLE_IMAGE_FORMATS);
                    this.handleSearch(URL.createObjectURL(imageBlob));
                } catch {
                    this.error = this.$t('imageSearch.errors.noImage');
                }
            },
            async onFileChange(event) {
                this.isReady = null;
                this.error = null;
                this.isProcessing = true;

                const image = event.target.files[0];

                if (!this.processImageType(image)) {
                    return;
                }
                
                try {
                    const base64 = await this.readAsBase64(image);
                    this.handleSearch(base64);
                } catch (error) {
                    this.error = String(error.message || error.statusText || error);
                }
            },
            onLinkChange(event) {
                const imageUrlRegex = /^https?:\/\/.*\/.*\.(png|gif|webp|jpeg|jpg|heic)\??.*$/gmi;
                this.$refs['image-picker'].value = '';
                const value = String(event.target.value).trim();
                const DELAY = 5000;

                this.error = null;

                if (this.linkTimer) {
                    clearTimeout(this.linkTimer);
                }

                this.linkTimer = setTimeout(async () => {
                    if (!value) {
                        this.loadByUrlStatus = {
                            loading: false,
                            error: null,
                        };

                        return;
                    }
                    if (!/^http/.test(value)) {
                        this.loadByUrlStatus = {
                            loading: false,
                            error: this.$t('imageSearch.errors.invalidUrlHTTP'),
                        };

                        debounce(this.imageSearchUseIncorrectLinkClick(event, value), DELAY);
                        return;
                    }
                    if (!value.match(imageUrlRegex)) {
                        this.loadByUrlStatus = {
                            loading: false,
                            error: this.$t('imageSearch.errors.invalidUrl'),
                        };

                        debounce(this.imageSearchUseIncorrectLinkClick(event, value), DELAY);
                        return;
                    }

                    this.loadByUrlStatus = {
                        loading: true,
                        error: null,
                    };

                    try {
                        const fileExtension = value.match(/\.[0-9a-z]+(?:\?[^\s]+)?$/i)[0].replace('.', 'image/');
                        const blob = await this.loadImageToBlobByUrl(value);
                        this.$emit('image-search-use-correct-link-click', { type: 'imageSearchUseCorrectLink', event, text: value });
                        this.readAsBase64(blob)
                            .then(base64 => {
                                const modifyBase64String = base64.replace('application/octet-stream', fileExtension);
                                this.handleSearch(modifyBase64String);
                            })
                            .catch(error => {
                                this.loadByUrlStatus = {
                                    loading: false,
                                    error: String(error.message || error.statusText || error),
                                };
                            });
                    } catch (error) {
                        this.loadByUrlStatus = {
                            loading: false,
                            error: String(error.message || error.title || error),
                        };
                    }
                }, HANDLE_LINK_CHANGE_DELAY);
            },
            imageSearchUseIncorrectLinkClick(event, value) {
                this.$emit('image-search-use-incorrect-link-click', {
                    type: 'imageSearchUseIncorrectLink',
                    text: value,
                    event,
                });
            },
            handleSearch(image) {
                document.removeEventListener('keypress', this.searchOnEnter);
                /*
                 * TODO Возможно можно удалить
                 * this.$emit('image-search-button-search-click', { type: 'imageSearchButtonSearch', event });
                 */

                this.$emit('search', image);
                this.clearState();
            },
            searchOnEnter(event) {
                if (event.code === 'Enter') {
                    this.handleSearchCropped();
                }
            },
            loadImageToBlobByUrl(url) {
                const paramsPart = {
                    i: url,
                };

                return fetch(`${IMAGE_PROXY_URL}?${new URLSearchParams(paramsPart).toString()}`)
                    .then(response => response.body)
                    .then(stream => new Response(stream))
                    .then(response => response.blob());
            },
            clearState() {
                this.clearLoadImageByUrlError();
                this.error = null;
            },
        },
    };
</script>
